import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingBag, Upload, ImageIcon, Loader2, Sparkles, CheckCircle2, Trash2, Save, AlertTriangle } from "lucide-react";
import GuideLink from "@/components/GuideLink";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  uploadAsset, createSalesVideo, listSalesVideos, deleteSalesVideo,
  generateSalesConcept, getSettingsStatus, updateSalesConcept, getSalesVideo, generateSalesVideo, reconcileSalesVideo, getAssetBlob, getVideoModels,
  getSalesVideoTemplates, previewSalesVideo,
} from "@/lib/api";
import { isActive, isMutationLocked, isOlderSnapshot, needsReview, statusLabel } from "@/lib/salesVideoStatus";
import { useAuth } from "@/context/AuthContext";
import { draftStorageKey, readPendingDraft, persistPendingDraft, newDraftRequestId, imageFingerprint, sameDraftIntent } from "@/lib/salesVideoDraft";
import { isCurrentSalesPreview } from "@/lib/salesVideoUx";

const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED = ["image/jpeg", "image/jpg", "image/png"];
const GENDERS = [
  { value: "female", label: "Wanita" },
  { value: "male", label: "Pria" },
  { value: "other", label: "Lainnya" },
];

const validate = (f) => {
  if (!f) return "Silakan pilih foto.";
  if (f.type ? !ALLOWED.includes(f.type) : !/\.(jpe?g|png)$/i.test(f.name)) return "Gunakan JPG, JPEG, atau PNG.";
  if (f.size === 0) return "Foto tidak boleh kosong.";
  if (f.size > MAX_BYTES) return "Ukuran foto maksimal 10 MB.";
  return null;
};

const voiceLabel = (v) => ({ female: "Wanita", male: "Pria", other: "Manual" }[v] || "-");
const defaultVideoModelKey = (models) => models.find((model) => model.enabled !== false && model.is_default)?.model_key ||
  models.find((model) => model.enabled !== false)?.model_key || "";
const apiErrorMessage = (error, fallback) => {
  if (error?.response?.status === 402) return "Kredit tidak cukup. Isi kredit lalu periksa status sebelum melanjutkan.";
  const detail = error?.response?.data?.detail;
  return typeof detail === "string" && detail.trim() ? detail : fallback;
};

function PhotoPicker({ label, testid, file, setFile, preview, setPreview }) {
  const inputRef = useRef(null);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);
  const onChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const err = validate(f);
    if (err) { toast.error(err); return; }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };
  return (
    <div>
      <Label className="text-sm font-medium text-slate-700">{label}</Label>
      <div
        data-testid={`${testid}-dropzone`}
        onClick={() => inputRef.current?.click()}
        className="mt-2 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/60 px-4 py-8 text-center transition-colors hover:border-blue-400 hover:bg-blue-50/40"
      >
        {preview ? (
          <img src={preview} alt={label} className="h-32 w-32 rounded-lg object-cover shadow-sm" />
        ) : (
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white text-slate-400 shadow-sm">
            <ImageIcon className="h-7 w-7" />
          </span>
        )}
        <span className="flex items-center gap-1.5 text-sm font-medium text-blue-600">
          <Upload className="h-4 w-4" /> {file ? "Ganti foto" : "Upload foto"}
        </span>
        <span className="text-xs text-slate-400">JPG / PNG, maks 10 MB</span>
      </div>
      <input ref={inputRef} type="file" accept="image/png,image/jpeg" className="hidden" data-testid={`${testid}-input`} onChange={onChange} />
    </div>
  );
}

const Chips = ({ items }) => (
  <div className="flex flex-wrap gap-1.5">
    {(items || []).map((t, i) => (
      <span key={i} className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">{t}</span>
    ))}
    {(!items || items.length === 0) && <span className="text-sm text-slate-400">-</span>}
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
    <div className="mt-1 text-sm text-slate-700">{children}</div>
  </div>
);

function ConceptView({ sv, onSaved, locked, onEditing }) {
  const c = sv.concept || {};
  const [salesAngle, setSalesAngle] = useState(c.sales_angle || "");
  const [script, setScript] = useState(c.short_script || "");
  const [cta, setCta] = useState(c.cta || "");
  const [videoPrompt, setVideoPrompt] = useState(c.video_prompt || "");
  const [creativeDirection, setCreativeDirection] = useState(c.creative_direction || "");
  const [saving, setSaving] = useState(false);
  const saveGuard = useRef(false);

  useEffect(() => {
    setSalesAngle(c.sales_angle || "");
    setScript(c.short_script || "");
    setCta(c.cta || "");
    setVideoPrompt(c.video_prompt || "");
    setCreativeDirection(c.creative_direction || "");
  }, [sv.id]); // eslint-disable-line

  const dirty = salesAngle !== (c.sales_angle || "") || script !== (c.short_script || "") || cta !== (c.cta || "") ||
    videoPrompt !== (c.video_prompt || "") || creativeDirection !== (c.creative_direction || "");

  useEffect(() => { onEditing(dirty || saving); return () => onEditing(false); }, [dirty, saving, onEditing]);

  const save = async () => {
    if (locked || saving || saveGuard.current) return;
    if (salesAngle.trim() === "" || script.trim() === "" || cta.trim() === "" || videoPrompt.trim() === "") {
      toast.error("Sales angle, script, CTA, dan prompt video tidak boleh kosong.");
      return;
    }
    saveGuard.current = true;
    setSaving(true);
    try {
      const updated = await updateSalesConcept(sv.id, {
        sales_angle: salesAngle.trim(), short_script: script.trim(), cta: cta.trim(),
        video_prompt: videoPrompt.trim(), creative_direction: creativeDirection.trim(),
      });
      setSalesAngle(updated.concept?.sales_angle || "");
      setScript(updated.concept?.short_script || "");
      setCta(updated.concept?.cta || "");
      setVideoPrompt(updated.concept?.video_prompt || "");
      setCreativeDirection(updated.concept?.creative_direction || "");
      onSaved(updated);
      toast.success("Perubahan tersimpan.");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal menyimpan perubahan."));
    } finally {
      saveGuard.current = false;
      setSaving(false);
    }
  };

  return (
    <div className="mt-6 rounded-2xl border border-emerald-200 bg-white p-6 shadow-sm" data-testid="concept-view">
      <div className="mb-4 flex items-center gap-2 text-emerald-700">
        <CheckCircle2 className="h-5 w-5" />
        <p className="font-semibold">Konsep video siap.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Pemahaman Produk"><span data-testid="concept-understanding">{c.product_understanding}</span></Field>
        <Field label="Target"><span data-testid="concept-target">{c.target_context}</span></Field>
        <Field label="Key Features"><Chips items={c.key_features} /></Field>
        <Field label="Key Benefits"><Chips items={c.key_benefits} /></Field>
        <Field label="Hook"><span data-testid="concept-hook">{c.hook}</span></Field>
        <Field label="Aksi Produk"><span data-testid="concept-action">{c.product_action}</span></Field>
        <Field label="Arahan Kreatif"><span>{c.creative_direction}</span></Field>
        <Field label="Voice">
          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700" data-testid="concept-voice">{voiceLabel(c.voice_category)}</span>
        </Field>
      </div>

      <div className="mt-5 rounded-xl bg-slate-50 p-4">
        <Field label="Prompt Video"><p className="whitespace-pre-wrap" data-testid="concept-video-prompt">{c.video_prompt}</p></Field>
        <p className="mt-2 text-xs text-slate-500">Generasi menggunakan prompt video tersimpan. Edit prompt video di bawah untuk mengubah arahan visual sebelum Generate.</p>
      </div>
      <div className="mt-5 space-y-4 rounded-xl bg-slate-50 p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Bisa diedit</p>
        <div>
          <Label className="text-sm text-slate-600">Sales Angle</Label>
          <Input className="mt-1" data-testid="edit-sales-angle" disabled={locked} value={salesAngle} onChange={(e) => setSalesAngle(e.target.value)} />
        </div>
        <div>
          <Label className="text-sm text-slate-600">Script</Label>
          <Textarea className="mt-1 resize-none" rows={4} data-testid="edit-script" disabled={locked} value={script} onChange={(e) => setScript(e.target.value)} />
        </div>
        <div>
          <Label className="text-sm text-slate-600">CTA</Label>
          <Input className="mt-1" data-testid="edit-cta" disabled={locked} value={cta} onChange={(e) => setCta(e.target.value)} />
        </div>
        <div>
          <Label className="text-sm text-slate-600">Arahan Kreatif</Label>
          <Textarea className="mt-1 resize-none" rows={2} data-testid="edit-creative-direction" disabled={locked} value={creativeDirection} onChange={(e) => setCreativeDirection(e.target.value)} />
        </div>
        <div>
          <Label className="text-sm text-slate-600">Prompt Video</Label>
          <Textarea className="mt-1 resize-none" rows={5} data-testid="edit-video-prompt" disabled={locked} value={videoPrompt} onChange={(e) => setVideoPrompt(e.target.value)} />
        </div>
        <Button onClick={save} disabled={!dirty || saving || locked} data-testid="save-concept-btn" className="bg-emerald-600 hover:bg-emerald-700">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Simpan Perubahan
        </Button>
      </div>
    </div>
  );
}

export default function VideoJualan() {
  const { user } = useAuth();
  const location = useLocation();
  const queryProjectId = new URLSearchParams(location.search).get("project");
  const requestedProjectId = /^[a-f0-9]{24}$/i.test(queryProjectId || "") ? queryProjectId : null;
  const [productFile, setProductFile] = useState(null);
  const [productPreview, setProductPreview] = useState(null);
  const [modelFile, setModelFile] = useState(null);
  const [modelPreview, setModelPreview] = useState(null);
  const [modelGender, setModelGender] = useState("female");
  const [description, setDescription] = useState("");
  const [creating, setCreating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sv, setSv] = useState(null); // current sales_video (draft or concept_ready)
  const [error, setError] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [videoBusy, setVideoBusy] = useState(false);
  const [conceptEditing, setConceptEditing] = useState(false);
  const [uncertainId, setUncertainId] = useState(null);
  const [videoObjectUrl, setVideoObjectUrl] = useState(null);
  const [mediaError, setMediaError] = useState(false);
  const [mediaAttempt, setMediaAttempt] = useState(0);
  const [videoModels, setVideoModels] = useState([]);
  const videoModelsRef = useRef(videoModels);
  videoModelsRef.current = videoModels;
  const [selectedModelKey, setSelectedModelKey] = useState("");
  const [modelsLoading, setModelsLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templatesError, setTemplatesError] = useState(false);
  const [templatesAttempt, setTemplatesAttempt] = useState(0);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const previewRequest = useRef(0);
  const previewGuard = useRef(false);
  const generationGuard = useRef(false);
  const creationGuard = useRef(false);
  const conceptGuard = useRef(false);
  const selectedId = useRef(null);
  const currentJob = useRef(null);
  const jobRequest = useRef(0);
  const draftsRequest = useRef(0);
  const pendingDraft = useRef(null);
  const draftScope = useRef(null);
  const authUserId = useRef(user?.id);
  authUserId.current = user?.id;
  const draftRequestStorageKey = draftStorageKey(user?.id);
  const selectedJobStorageKey = user?.id ? `sales-video-selected:${user.id}` : null;
  const mounted = useRef(false);
  const locked = (uncertainId !== null && uncertainId === sv?.id) || videoBusy || isMutationLocked(sv);
  const selectJob = useCallback((job) => {
    if (job && job.user_id !== authUserId.current) return;
    jobRequest.current += 1;
    selectedId.current = job?.id || null;
    currentJob.current = job;
    setSv(job);
    if (!job && selectedJobStorageKey) { try { sessionStorage.removeItem(selectedJobStorageKey); } catch { /* Storage may be unavailable. */ } }
  }, [selectedJobStorageKey]);
  const applyJob = useCallback((job, request) => {
    if (!mounted.current || job.user_id !== authUserId.current || selectedId.current !== job.id || request !== jobRequest.current || isOlderSnapshot(currentJob.current, job)) return false;
    currentJob.current = job;
    setSv(job);
    setUncertainId((id) => id === job.id ? null : id);
    setDrafts((current) => current.map((draft) => draft.id === job.id && !isOlderSnapshot(draft, job) ? job : draft));
    return true;
  }, []);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  // AI planning (concept) is only offered when the server says it is open; null = not yet known (the button stays usable, the server still refuses safely).
  const [aiTextOpen, setAiTextOpen] = useState(null);
  useEffect(() => {
    let cancelled = false;
    getSettingsStatus().then((status) => { if (!cancelled) setAiTextOpen(status?.ai_text?.enabled !== false); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    draftScope.current = draftRequestStorageKey;
    jobRequest.current += 1;
    draftsRequest.current += 1;
    selectedId.current = null;
    currentJob.current = null;
    setSv(null);
    setDrafts([]);
    setUncertainId(null);
    setProductFile(null);
    setProductPreview(null);
    setModelFile(null);
    setModelPreview(null);
    try { pendingDraft.current = readPendingDraft(sessionStorage, draftRequestStorageKey); }
    catch { pendingDraft.current = null; }
    setDescription(pendingDraft.current?.payload.product_description || "");
    setSelectedModelKey(pendingDraft.current?.payload.model_key || defaultVideoModelKey(videoModelsRef.current));
    setSelectedTemplateId(pendingDraft.current?.payload.template_id || "");
  }, [draftRequestStorageKey]);
  useEffect(() => {
    let cancelled = false;
    setTemplatesLoading(true);
    setTemplatesError(false);
    getSalesVideoTemplates().then((data) => {
      if (!cancelled) setTemplates((data.templates || []).filter((template) => template.active === true));
    }).catch(() => { if (!cancelled) { setTemplates([]); setTemplatesError(true); } })
      .finally(() => { if (!cancelled) setTemplatesLoading(false); });
    return () => { cancelled = true; };
  }, [draftRequestStorageKey, templatesAttempt]);
  useEffect(() => {
    previewRequest.current += 1;
    setPreview(null);
    setPreviewError("");
    setPreviewLoading(false);
  }, [sv?.id, sv?.updated_at, conceptEditing, draftRequestStorageKey]);
  useEffect(() => {
    let cancelled = false;
    setModelsLoading(true);
    getVideoModels().then((data) => {
      if (cancelled) return;
      const available = (data.models || []).filter((model) => model.enabled !== false);
      setVideoModels(available);
      setSelectedModelKey(pendingDraft.current?.payload.model_key || defaultVideoModelKey(available));
    }).catch(() => { if (!cancelled) toast.error("Gagal memuat model video."); })
      .finally(() => { if (!cancelled) setModelsLoading(false); });
    return () => { cancelled = true; };
  }, [draftRequestStorageKey]);

  // Restore selection using GET only; refreshing never submits a generation.
  useEffect(() => {
    if (!selectedJobStorageKey) return;
    let id;
    let legacy = false;
    try {
      id = requestedProjectId || sessionStorage.getItem(selectedJobStorageKey);
      if (!id) { id = sessionStorage.getItem("sales-video-selected"); legacy = Boolean(id); }
    } catch { /* Storage may be unavailable. */ }
    let cancelled = false;
    if (id) {
      currentJob.current = null;
      setSv(null);
      selectedId.current = id;
      const request = ++jobRequest.current;
      getSalesVideo(id).then((job) => {
        if (!cancelled && applyJob(job, request) && legacy) {
          try {
            // Migrate a legacy selection only after the API verifies ownership.
            sessionStorage.setItem(selectedJobStorageKey, id);
            sessionStorage.removeItem("sales-video-selected");
          } catch { /* Storage may be unavailable. */ }
        }
      }).catch(() => { if (!cancelled && requestedProjectId) toast.error("Project tidak tersedia atau tidak dapat diakses."); });
    }
    return () => { cancelled = true; };
  }, [applyJob, selectedJobStorageKey, requestedProjectId]);
  useEffect(() => {
    if (selectedJobStorageKey && sv?.user_id === user?.id && sv?.id) { try { sessionStorage.setItem(selectedJobStorageKey, sv.id); } catch { /* Storage may be unavailable. */ } }
  }, [sv?.id, sv?.user_id, user?.id, selectedJobStorageKey]);

  const monitoring = videoBusy || isActive(sv) || needsReview(sv) || (uncertainId !== null && uncertainId === sv?.id);
  // The monitor reads ONLY these four fields of the job (needsReview looks at status, provider_status and credit_status). Rebuilding the
  // job view from the primitives that are already the dependencies keeps the exact re-run moments: a new object with the same values must
  // not restart the timer.
  const svId = sv?.id;
  const svStatus = sv?.status;
  const svProviderStatus = sv?.provider_status;
  const svCreditStatus = sv?.credit_status;
  useEffect(() => {
    const current = { status: svStatus, provider_status: svProviderStatus, credit_status: svCreditStatus };
    if (!svId || videoBusy || !monitoring || (!needsReview(current) && ["completed", "failed"].includes(current.status))) return;
    let cancelled = false;
    let timer;
    const id = svId;
    const tick = async () => {
      try {
        const request = ++jobRequest.current;
        const job = await getSalesVideo(id);
        if (cancelled || selectedId.current !== id) return;
        if (applyJob(job, request)) {
          if (job.credit_status !== current.credit_status) window.dispatchEvent(new Event("credits-updated"));
          if (!needsReview(job) && ["completed", "failed"].includes(job.status)) {
            window.dispatchEvent(new Event("credits-updated"));
            return;
          }
        }
      } catch { /* A failed GET does not imply a failed generation. */ }
      if (!cancelled) timer = setTimeout(tick, needsReview(current) ? 7000 : 3000);
    };
    timer = setTimeout(tick, 1000);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [svId, svStatus, svProviderStatus, svCreditStatus, monitoring, videoBusy, applyJob]);

  useEffect(() => {
    let cancelled = false;
    let objectUrl;
    setVideoObjectUrl(null);
    setMediaError(false);
    if (sv?.status === "completed" && sv.video_url === `/api/assets/${sv.id}`) {
      getAssetBlob(sv.id).then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setVideoObjectUrl({ id: sv.id, url: objectUrl });
      }).catch(() => { if (!cancelled) setMediaError(true); });
    }
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [sv?.id, sv?.status, sv?.video_url, mediaAttempt]);

  const handleGenerateVideo = async () => {
    if (!sv || selectedId.current !== sv.id || (!canStart && !canResume) || uncertainId === sv.id || creating || videoBusy || generating || conceptEditing || generationGuard.current || creationGuard.current || conceptGuard.current || !["kie_grok", "kie"].includes(sv.provider)) return;
    if (canStart && (!isCurrentSalesPreview(preview, sv, conceptEditing) || previewLoading || previewGuard.current)) return;
    const id = sv.id;
    generationGuard.current = true;
    jobRequest.current += 1;
    setVideoBusy(true);
    try {
      // Only a fresh/prepared job can submit. Existing or uncertain jobs use
      // reconciliation, whose backend contract never creates a provider task.
      if (canStart) await generateSalesVideo(id);
      else await reconcileSalesVideo(id);
      // Read the public detail instead of retaining provider internals from POST.
      const request = selectedId.current === id ? ++jobRequest.current : -1;
      const job = await getSalesVideo(id);
      applyJob(job, request);
      if (mounted.current) loadDrafts();
    } catch (e) {
      if (mounted.current && selectedId.current === id) setUncertainId(id);
      if (mounted.current) toast.error(apiErrorMessage(e, "Permintaan terputus. Periksa status; jangan membuat draft baru untuk mengulang."));
      try {
        const request = selectedId.current === id ? ++jobRequest.current : -1;
        const job = await getSalesVideo(id);
        applyJob(job, request);
      } catch { /* Keep monitoring; never automatically POST again. */ }
    } finally {
      generationGuard.current = false;
      if (mounted.current) {
        setVideoBusy(false);
        // Reserving and releasing also change the available balance.
        window.dispatchEvent(new Event("credits-updated"));
      }
    }
  };

  const handlePreview = async () => {
    if (!sv || selectedId.current !== sv.id || !canStart || conceptEditing || creating || generating || videoBusy || previewGuard.current || creationGuard.current || conceptGuard.current || generationGuard.current) return;
    const id = sv.id;
    const updatedAt = sv.updated_at;
    const scope = authUserId.current;
    const request = ++previewRequest.current;
    previewGuard.current = true;
    setPreviewLoading(true);
    setPreviewError("");
    try {
      const data = await previewSalesVideo(id);
      if (mounted.current && authUserId.current === scope && selectedId.current === id && currentJob.current?.updated_at === updatedAt && previewRequest.current === request) {
        setPreview({ ...data, id });
      }
    } catch (error) {
      if (mounted.current && selectedId.current === id && authUserId.current === scope && previewRequest.current === request) {
        setPreview(null);
        setPreviewError(apiErrorMessage(error, "Estimation belum dapat dihitung dengan andal. Muat ulang preview sebelum Generate."));
      }
    } finally {
      previewGuard.current = false;
      if (mounted.current && previewRequest.current === request) setPreviewLoading(false);
    }
  };

  const loadDrafts = useCallback(() => {
    const scope = draftRequestStorageKey;
    const request = ++draftsRequest.current;
    return listSalesVideos().then((d) => {
      if (!mounted.current || draftScope.current !== scope || draftStorageKey(authUserId.current) !== scope || request !== draftsRequest.current) return;
      setDrafts((current) => (d.sales_videos || []).map((draft) => {
        const existing = current.find((job) => job.id === draft.id);
        return isOlderSnapshot(existing, draft) ? existing : draft;
      }));
    }).catch(() => {});
  }, [draftRequestStorageKey]);
  useEffect(() => { loadDrafts(); }, [loadDrafts]);

  const handleCreateDraft = async () => {
    if (creationGuard.current || conceptGuard.current || generationGuard.current || creating || videoBusy || (uncertainId !== null && uncertainId === sv?.id) || generating || conceptEditing || isActive(sv) || needsReview(sv)) return;
    const previous = pendingDraft.current;
    if (!productFile && !previous) { toast.error("Silakan upload foto produk."); return; }
    const selectedModel = videoModels.find((model) => model.model_key === selectedModelKey && model.enabled !== false);
    if (description.trim() === "") { toast.error("Deskripsi produk wajib diisi."); return; }
    creationGuard.current = true;
    setCreating(true);
    setError(false);
    selectJob(null);
    try {
      const intent = {
        product_hash: productFile ? await imageFingerprint(productFile) : previous.product_hash,
        model_hash: modelFile ? await imageFingerprint(modelFile) : previous?.model_hash || null,
        model_key: selectedModelKey, product_description: description.trim(),
        template_id: selectedTemplateId,
      };
      let receipt = previous;
      if (!sameDraftIntent(previous, intent)) {
        if (!selectedModel) { toast.error("Model video aktif belum tersedia."); return; }
        const prod = previous?.product_hash === intent.product_hash ? { asset_id: previous.payload.product_asset_id } : await uploadAsset(productFile, "product");
        const model = previous?.model_hash === intent.model_hash ? { asset_id: previous?.payload.model_asset_id } : modelFile ? await uploadAsset(modelFile, "sales_model") : null;
        receipt = { version: 1, product_hash: intent.product_hash, model_hash: intent.model_hash, payload: {
          client_request_id: newDraftRequestId(), product_asset_id: prod.asset_id, model_asset_id: model?.asset_id || null,
          provider: selectedModel.provider, model_key: intent.model_key, product_description: intent.product_description,
          ...(intent.template_id ? { template_id: intent.template_id } : {}),
        } };
      }
      // Persist before POST. A lost response or refresh can reuse this request
      // and its already-uploaded assets without creating another draft.
      if (draftScope.current !== draftRequestStorageKey || authUserId.current !== user?.id) throw new Error("Sesi pengguna berubah. Muat ulang sebelum melanjutkan.");
      persistPendingDraft(sessionStorage, draftRequestStorageKey, receipt);
      pendingDraft.current = receipt;
      const draft = await createSalesVideo(receipt.payload);
      if (!draft?.id) throw new Error("Respons draft belum dapat dipastikan. Coba lagi untuk memeriksa permintaan yang sama.");
      if (draftScope.current === draftRequestStorageKey && authUserId.current === user?.id) pendingDraft.current = null;
      try { sessionStorage.removeItem(draftRequestStorageKey); } catch { /* A retained receipt remains idempotent. */ }
      if (mounted.current && draftScope.current === draftRequestStorageKey && authUserId.current === user?.id) {
        selectJob(draft);
        toast.success("Draft dibuat. Lanjut buat konsep.");
        loadDrafts();
      }
    } catch (e) {
      if (mounted.current && draftScope.current === draftRequestStorageKey && authUserId.current === user?.id) toast.error(apiErrorMessage(e, e?.message || "Gagal membuat draft. Silakan coba lagi."));
    } finally {
      creationGuard.current = false;
      if (mounted.current) setCreating(false);
    }
  };

  const handleGenerateConcept = async () => {
    if (!sv || selectedId.current !== sv.id || locked || creating || generating || creationGuard.current || conceptGuard.current || generationGuard.current) return;
    const id = sv.id;
    const request = ++jobRequest.current;
    conceptGuard.current = true;
    setGenerating(true);
    setError(false);
    try {
      const updated = await generateSalesConcept(id, { model_gender: modelGender });
      if (applyJob(updated, request)) toast.success("Konsep video siap.");
      if (mounted.current) loadDrafts();
    } catch (e) {
      if (mounted.current && selectedId.current === id && request === jobRequest.current) {
        setError(true);
        toast.error(apiErrorMessage(e, "Terjadi kesalahan. Silakan coba lagi."));
      }
    } finally {
      conceptGuard.current = false;
      if (mounted.current) setGenerating(false);
    }
  };

  const handleDelete = async (id) => {
    const job = sv?.id === id ? sv : drafts.find((d) => d.id === id);
    if (creationGuard.current || conceptGuard.current || generationGuard.current || uncertainId === id || isMutationLocked(job)) return;
    if (!window.confirm("Hapus project ini dari riwayat? Catatan transaksi dan kredit tetap disimpan.")) return;
    try {
      await deleteSalesVideo(id);
      if (selectedId.current === id) selectJob(null);
      toast.success("Draft dihapus.");
      loadDrafts();
    } catch { toast.error("Gagal menghapus draft."); }
  };

  const openDraft = async (d) => {
    if (creationGuard.current) return;
    const job = selectedId.current === d.id && isOlderSnapshot(currentJob.current, d) ? currentJob.current : d;
    selectJob(job); setError(false);
    const request = ++jobRequest.current;
    try {
      const job = await getSalesVideo(d.id);
      applyJob(job, request);
    } catch { if (mounted.current && selectedId.current === d.id) toast.error("Gagal mengambil status terbaru."); }
  };

  const checkStatus = async () => {
    if (!sv || selectedId.current !== sv.id || creating || generating || conceptEditing || videoBusy || creationGuard.current || conceptGuard.current || generationGuard.current) return;
    const id = sv.id;
    generationGuard.current = true;
    const request = ++jobRequest.current;
    setVideoBusy(true);
    try {
      if (["kie_grok", "kie"].includes(sv.provider) && (sv.provider_status || needsReview(sv))) await reconcileSalesVideo(id);
      const job = await getSalesVideo(id);
      applyJob(job, request);
    } catch (error) { if (mounted.current) toast.error(apiErrorMessage(error, "Status belum dapat dipastikan. Periksa kembali nanti.")); }
    finally {
      generationGuard.current = false;
      if (mounted.current) {
        setVideoBusy(false);
        window.dispatchEvent(new Event("credits-updated"));
      }
    }
  };

  const conceptReady = Boolean(sv?.concept);
  const playableVideoUrl = videoObjectUrl?.id === sv?.id && sv?.status === "completed" ? videoObjectUrl?.url : null;
  const canStart = sv?.status === "concept_ready" && !needsReview(sv) && (
    !sv.provider_status || (sv.provider_status === "prepared" && [undefined, null, "quoted"].includes(sv.credit_status))
  );
  const canResume = !canStart && (needsReview(sv) || (
    ["prepared", "processing", "completed"].includes(sv?.provider_status) && sv?.status !== "completed" &&
    (sv?.status !== "failed" || sv?.provider_status === "completed")
  ));
  const previewReady = isCurrentSalesPreview(preview, sv, conceptEditing);
  const selectedTemplate = templates.find((template) => template.id === selectedTemplateId);

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 md:px-8" data-testid="sales-video-page">
      <div className="mb-6 flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600 text-white">
          <ShoppingBag className="h-5 w-5" />
        </span>
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Video Jualan</h1>
          <p className="text-sm text-slate-500">Buat konsep video jualan dari foto produk & model Anda.</p>
          {aiTextOpen === false && <p role="status" className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="ai-text-disabled-notice">Pembuatan konsep dengan AI belum diaktifkan. Anda masih bisa melihat draft dan riwayat; konsep baru belum bisa dibuat sampai fitur ini dibuka.</p>}
          <Link to="/riwayat" className="mt-1 inline-block text-sm font-medium text-blue-700">Riwayat Video / Project</Link>
          <GuideLink id="video-jualan" className="ml-3" />
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="mb-5" data-testid="sales-template-picker">
          <Label htmlFor="sales-template" className="text-sm font-medium text-slate-700">Template Video Jualan</Label>
          <select id="sales-template" data-testid="sales-template-select" value={selectedTemplateId} onChange={(event) => setSelectedTemplateId(event.target.value)}
            disabled={templatesLoading || creating} className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700">
            <option value="">{templatesLoading ? "Memuat template…" : "Tanpa template — buat konsep sendiri"}</option>
            {pendingDraft.current?.payload.template_id && !templates.some((template) => template.id === selectedTemplateId) && <option value={selectedTemplateId}>Template permintaan draft sebelumnya</option>}
            {templates.map((template) => <option key={template.id} value={template.id}>{template.name}</option>)}
          </select>
          {templatesError && <p role="alert" className="mt-2 text-sm text-amber-700">Template gagal dimuat. <button className="underline" onClick={() => setTemplatesAttempt((attempt) => attempt + 1)}>Coba kembali</button></p>}
          {!templatesLoading && !templatesError && !templates.length && <p className="mt-2 text-xs text-slate-500">Belum ada template aktif.</p>}
          {selectedTemplate && <div className="mt-3 rounded-xl bg-blue-50 p-3 text-sm text-slate-700" data-testid="selected-template-summary">
            <p>{selectedTemplate.description}</p>
            <p className="mt-2 text-xs text-slate-500">{selectedTemplate.default_duration_seconds} detik · {selectedTemplate.config?.concept_defaults?.creative_direction}</p>
            <p className="mt-2 text-xs text-blue-700">Template mengisi konsep draft. Anda dapat mengedit konsep dan prompt video sebelum Generate.</p>
          </div>}
        </div>
        <div className="mb-5">
          <Label htmlFor="video-model" className="text-sm font-medium text-slate-700">Model Video</Label>
          <select id="video-model" value={selectedModelKey} onChange={(event) => setSelectedModelKey(event.target.value)}
            disabled={modelsLoading || creating} data-testid="video-model-select"
            className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700">
            {!videoModels.length && <option value="">{modelsLoading ? "Memuat model…" : "Belum ada model aktif"}</option>}
            {pendingDraft.current && !videoModels.some((model) => model.model_key === selectedModelKey) && <option value={selectedModelKey}>Model draft sebelumnya</option>}
            {videoModels.map((model) => <option key={model.model_key} value={model.model_key}>{model.display_name}{model.is_default ? " (Default)" : ""}</option>)}
          </select>
          <p className="mt-1 text-xs text-slate-500">6 detik · 720p · 9:16. Model yang dipilih tersimpan untuk draft baru.</p>
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          <PhotoPicker label="1. Foto Produk" testid="product" file={productFile} setFile={setProductFile} preview={productPreview} setPreview={setProductPreview} />
          <PhotoPicker label="2. Foto Model (Opsional)" testid="model" file={modelFile} setFile={setModelFile} preview={modelPreview} setPreview={setModelPreview} />
        </div>
        {pendingDraft.current && !productFile && <p className="mt-2 text-sm text-amber-700">Foto dari permintaan draft sebelumnya tersimpan. Buat Draft Video akan memeriksa permintaan yang sama; upload foto baru untuk menggantinya.</p>}

        <div className="mt-5">
          <Label className="text-sm font-medium text-slate-700">3. Jenis Model</Label>
          <div className="mt-2 flex gap-2" data-testid="model-gender-group">
            {GENDERS.map((g) => (
              <button
                key={g.value}
                type="button"
                data-testid={`gender-${g.value}`}
                onClick={() => setModelGender(g.value)}
                className={`flex-1 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${
                  modelGender === g.value
                    ? "border-blue-600 bg-blue-50 text-blue-700"
                    : "border-slate-200 text-slate-600 hover:bg-slate-50"
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5">
          <Label htmlFor="desc" className="text-sm font-medium text-slate-700">4. Deskripsi Produk</Label>
          <Textarea
            id="desc"
            data-testid="product-description-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={6000}
            rows={5}
            className="mt-2 resize-none"
            placeholder={"Produk: tumbler stainless 500ml.\nKelebihan: tahan panas dan dingin, tidak mudah bocor, desain minimalis.\nTarget: pria dan wanita usia 20–40 tahun."}
          />
        </div>

        <Button
          data-testid="create-draft-btn"
          onClick={handleCreateDraft}
          disabled={modelsLoading || !selectedModelKey || creating || (uncertainId !== null && uncertainId === sv?.id) || generating || conceptEditing || videoBusy || isActive(sv) || needsReview(sv)}
          className="mt-5 w-full bg-blue-600 py-6 text-base font-semibold hover:bg-blue-700"
        >
          {creating ? (<><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Membuat draft…</>) : (<><ShoppingBag className="mr-2 h-5 w-5" /> Buat Draft Video</>)}
        </Button>
      </div>

      {sv && (
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="draft-panel">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-slate-800">Draft Video Jualan</p>
              <p className="mt-0.5 text-xs text-slate-500" data-testid="draft-description">{sv.product_description}</p>
              {(sv.model_display_name || sv.model_snapshot?.display_name) && <p className="mt-1 text-xs text-slate-500" data-testid="draft-model">Model: {sv.model_display_name || sv.model_snapshot.display_name}</p>}
              {sv.template_snapshot?.name && <p className="mt-1 text-xs text-slate-500">Template: {sv.template_snapshot.name}</p>}
            </div>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600" data-testid="draft-status">
              {statusLabel(sv)}
            </span>
          </div>

          {!conceptReady && (
            <Button
              data-testid="generate-concept-btn"
              onClick={handleGenerateConcept}
              disabled={creating || generating || locked || aiTextOpen === false}
              className="mt-4 w-full bg-blue-600 py-5 font-semibold hover:bg-blue-700"
            >
              {generating ? (<><Loader2 className="mr-2 h-5 w-5 animate-spin" /> AI sedang membuat konsep video…</>) : (<><Sparkles className="mr-2 h-5 w-5" /> Buat Konsep</>)}
            </Button>
          )}

          {error && !generating && (
            <div className="mt-4 flex items-center gap-2 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="concept-error">
              <AlertTriangle className="h-4 w-4" /> Terjadi kesalahan. Silakan coba lagi.
            </div>
          )}
        </div>
      )}

      {conceptReady && <ConceptView key={sv.id} sv={sv} locked={locked} onEditing={setConceptEditing} onSaved={(u) => { if (selectedId.current === u.id) applyJob(u, ++jobRequest.current); loadDrafts(); }} />}

      {sv && (
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6" data-testid="video-generation-panel">
          <p className="font-semibold" data-testid="video-generation-status">{statusLabel(sv)}</p>
          {!["kie_grok", "kie"].includes(sv.provider) && <p className="mt-2 text-sm text-slate-600">Draft lama tetap tersedia. Buat draft baru untuk generasi Video Jualan.</p>}
          {uncertainId === sv.id && <p className="mt-2 text-sm text-amber-700">Respons belum dapat dipastikan. Periksa status sebelum melanjutkan; tidak ada pengiriman ulang otomatis.</p>}
          {sv.error_message && <p className={`mt-2 text-sm ${needsReview(sv) ? "text-amber-700" : "text-red-700"}`}>{sv.error_message}</p>}
          {(needsReview(sv) || sv.status === "failed") && <p className="mt-2 text-sm text-amber-700">Status video dan kredit sedang diperiksa. Gunakan Periksa Status untuk melanjutkan pemeriksaan atau penyimpanan hasil yang sudah tersedia.</p>}
          {canStart && <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50/50 p-4" data-testid="sales-generation-preview">
            <p className="text-sm font-semibold text-slate-800">Preview & estimasi kredit</p>
            <p className="mt-1 text-xs text-slate-500">Periksa konfigurasi dan prompt tersimpan sebelum konfirmasi Generate.</p>
            <Button variant="outline" className="mt-3" onClick={handlePreview} disabled={previewLoading || creating || generating || videoBusy || conceptEditing} data-testid="preview-sales-video-btn">
              {previewLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}{previewLoading ? "Memuat preview…" : "Preview & Estimasi Kredit"}
            </Button>
            {conceptEditing && <p className="mt-3 text-sm text-amber-700">Simpan perubahan konsep sebelum memuat preview.</p>}
            {previewError && <p role="alert" className="mt-3 text-sm text-amber-700">{previewError}</p>}
            {preview && <div className="mt-4" data-testid="sales-preview-summary">
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <div><dt className="text-xs text-slate-500">Template</dt><dd>{preview.template?.name || "Tanpa template"}</dd></div>
                <div><dt className="text-xs text-slate-500">Model</dt><dd>{preview.model?.display_name || "Belum tersedia"}</dd></div>
                <div><dt className="text-xs text-slate-500">Durasi</dt><dd>{preview.duration_seconds ? `${preview.duration_seconds} detik` : "Belum tersedia"}</dd></div>
                <div><dt className="text-xs text-slate-500">Resolusi</dt><dd>{preview.resolution || "Belum tersedia"}</dd></div>
                <div><dt className="text-xs text-slate-500">Aspect ratio</dt><dd>{preview.aspect_ratio || "Belum tersedia"}</dd></div>
                <div><dt className="text-xs text-slate-500">Estimasi kredit</dt><dd data-testid="sales-estimated-credit">{preview.reliable && Number.isSafeInteger(preview.estimated_credits) ? `${preview.estimated_credits} kredit` : "Belum dapat dihitung"}</dd></div>
              </dl>
              {preview.prompt && <p className="mt-4 whitespace-pre-wrap break-words rounded-lg bg-white p-3 text-xs text-slate-600" data-testid="sales-preview-prompt">{preview.prompt}</p>}
              {!previewReady && <p role="alert" className="mt-3 text-sm text-amber-700">{preview.warning || "Estimasi belum dapat dihitung dengan andal atau konfigurasi sudah berubah. Muat ulang preview sebelum Generate."}</p>}
              {preview.warning && previewReady && <p className="mt-3 text-sm text-amber-700">{preview.warning}</p>}
              <p className="mt-3 text-xs text-slate-500">{preview.reservation_notice || "Kredit akan di-reserve saat generation benar-benar dimulai."} Server memvalidasi harga saat Generate.</p>
            </div>}
          </div>}
          {["kie_grok", "kie"].includes(sv.provider) && (canStart || canResume) && (
            <Button className="mt-4" data-testid="generate-sales-video-btn" disabled={(canStart && (!previewReady || previewLoading)) || uncertainId === sv.id || creating || videoBusy || generating || conceptEditing || ["reserving", "settling", "releasing"].includes(sv.credit_status)} onClick={handleGenerateVideo}>
              {videoBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {canStart ? "Konfirmasi Generate Video" : "Lanjutkan Pemeriksaan / Penyimpanan"}
            </Button>
          )}
          <Button variant="ghost" className="mt-2" disabled={creating || generating || conceptEditing || videoBusy} data-testid="refresh-sales-video-btn" onClick={checkStatus}>{videoBusy ? "Memeriksa…" : "Periksa Status"}</Button>
          {sv.status === "completed" && sv.video_url && !playableVideoUrl && !mediaError && <p className="mt-3 text-sm">Memuat video…</p>}
          {mediaError && <p className="mt-3 text-sm text-red-700">Video gagal dimuat. <button className="underline" onClick={() => setMediaAttempt((n) => n + 1)}>Muat ulang video</button></p>}
          {playableVideoUrl && <div className="mt-4">
            <video controls src={playableVideoUrl} className="w-full rounded-xl" data-testid="sales-video-player" />
            <a href={playableVideoUrl} download={`video-jualan-${sv.id}.mp4`} className="mt-3 inline-block font-medium text-blue-700" data-testid="download-sales-video">Download MP4</a>
          </div>}
        </div>
      )}

      {drafts.length > 0 && (
        <div className="mt-8">
          <h2 className="mb-3 text-base font-semibold text-slate-800">Draft Tersimpan</h2>
          <div className="space-y-2" data-testid="sales-video-drafts-list">
            {drafts.map((d) => (
              <div key={d.id} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid={`draft-item-${d.id}`}>
                <button className="min-w-0 flex-1 text-left" onClick={() => openDraft(d)} data-testid={`open-draft-${d.id}`}>
                  <p className="truncate text-sm font-medium text-slate-800">{d.product_description}</p>
                  <p className="text-xs text-slate-400">Status: {statusLabel(d)}</p>
                </button>
                <Button variant="ghost" size="sm" className="text-slate-400 hover:bg-red-50 hover:text-red-600" data-testid={`delete-draft-${d.id}`} disabled={creating || generating || videoBusy || uncertainId === d.id || isMutationLocked(sv?.id === d.id ? sv : d)} onClick={() => handleDelete(d.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
